import { computed, reactive, ref, watch } from 'vue';

const MODULE_COPY = Object.freeze({
  sales: {
    singular: 'venda',
    plural: 'Vendas',
    subtitle: 'Acompanhe o funil e gerencie suas vendas de forma inteligente.',
    recordLabel: 'Venda',
  },
  proposals: {
    singular: 'proposta',
    plural: 'Propostas',
    subtitle: 'Negociações, documentação e contratos em um só lugar.',
    recordLabel: 'Proposta',
  },
  visits: {
    singular: 'visita',
    plural: 'Visitas',
    subtitle: 'Agenda, confirmação e resultado comercial de cada visita.',
    recordLabel: 'Visita',
  },
  postSale: {
    singular: 'processo de pós-venda',
    plural: 'Pós-venda',
    subtitle: 'Acompanhe responsáveis, prazos e marcos após a venda.',
    recordLabel: 'Processo',
  },
  finance: {
    singular: 'lançamento financeiro',
    plural: 'Financeiro',
    subtitle: 'Controle recebimentos, responsabilidades e conciliação.',
    recordLabel: 'Lançamento',
  },
  brokers: {
    singular: 'corretor',
    plural: 'Corretores',
    subtitle: 'Acompanhe desempenho e os dados da sua operação.',
    recordLabel: 'Corretor',
  },
  commissions: {
    singular: 'comissão',
    plural: 'Comissões',
    subtitle: 'Acompanhe acordos, rateios e o histórico financeiro.',
    recordLabel: 'Comissão',
  },
});

const actionLabel = Object.freeze({
  accept: 'Aceitar proposta',
  archive: 'Arquivar venda',
  addNote: 'Adicionar nota',
  assign: 'Atribuir responsável',
  cancel: 'Cancelar processo',
  checkIn: 'Confirmar check-in',
  complete: 'Concluir processo',
  confirmPayment: 'Confirmar pagamento',
  confirm: 'Confirmar visita',
  counter: 'Enviar contraproposta',
  decline: 'Recusar proposta',
  deliver: 'Entregar venda',
  finalize: 'Finalizar venda',
  finish: 'Finalizar visita',
  finalizeOutcome: 'Registrar resultado',
  markPaid: 'Marcar como paga',
  markPayable: 'Marcar como a pagar',
  publishAgreement: 'Publicar acordo',
  requestReview: 'Enviar para revisão',
  restore: 'Restaurar venda',
  reschedule: 'Remarcar visita',
  saveDraft: 'Salvar rascunho',
  transition: 'Atualizar status',
  update: 'Salvar alterações',
  withdraw: 'Retirar proposta',
});

const actionConfig = Object.freeze({
  sales: [
    ['saveDraft', 'can_save_draft'],
    ['requestReview', 'can_request_review'],
    ['deliver', 'can_deliver'],
    ['archive', 'can_archive'],
    ['restore', 'can_restore'],
    ['finalize', 'can_finalize'],
  ],
  proposals: [
    ['accept', 'can_accept'],
    ['counter', 'can_counter'],
    ['decline', 'can_decline'],
    ['withdraw', 'can_withdraw'],
  ],
  visits: [
    ['confirm', 'can_confirm'],
    ['checkIn', 'can_check_in'],
    ['finish', 'can_finish'],
    ['reschedule', 'can_reschedule'],
    ['finalizeOutcome', 'can_finalize_outcome'],
  ],
  postSale: [
    ['transition', 'can_manage'],
    ['complete', 'can_complete'],
    ['cancel', 'can_cancel'],
    ['assign', 'can_assign'],
    ['addNote', 'can_add_note'],
  ],
  finance: [
    ['transition', 'can_transition'],
    ['confirmPayment', 'can_confirm_payment'],
  ],
  commissions: [
    ['markPayable', 'can_mark_payable'],
    ['markPaid', 'can_mark_paid'],
    ['publishAgreement', 'can_publish'],
  ],
});

const asArray = value => (Array.isArray(value) ? value : []);
const stringValue = value => (value == null ? '' : String(value));

const pathValue = (value, path) =>
  path.split('.').reduce((current, key) => current?.[key], value);

const firstValue = (value, paths, fallback = '') => {
  const found = paths.map(path => pathValue(value, path)).find(Boolean);
  return found ?? fallback;
};

const money = value => {
  if (value == null || value === '') return '—';
  if (typeof value === 'object') {
    return (
      value.formatted ||
      value.display ||
      value.label ||
      money(value.amount ?? value.amount_cents ?? value.cents)
    );
  }

  const number = Number(value);
  if (Number.isNaN(number)) return String(value);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(number);
};

const dateTime = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
};

const countBy = (records, predicate) =>
  records.filter(record => predicate(stringValue(record.status))).length;

const idFor = record =>
  firstValue(record, ['id', 'pool_id', 'parcela_id', 'agreement_id']);

const recordTitle = (module, record) => {
  const titlePaths = {
    sales: ['title', 'address.title', 'reference', 'id'],
    proposals: [
      'title',
      'imovel.titulo',
      'imovel.title',
      'property.title',
      'reference',
      'id',
    ],
    visits: [
      'imovel.titulo',
      'imovel.title',
      'property.title',
      'address.title',
      'visit_code',
      'id',
    ],
    postSale: ['transaction.title', 'title', 'transaction.reference', 'id'],
    finance: [
      'processo.titulo',
      'processo.codigo',
      'dados_processo.titulo',
      'dados_processo.codigo',
      'parcela.endereco.logradouro',
      'endereco.logradouro',
      'id',
    ],
    brokers: ['name', 'user.name', 'broker.name', 'email', 'id'],
    commissions: [
      'title',
      'reference',
      'processo.codigo',
      'imovel_listing.title',
      'id',
    ],
  };

  const title = stringValue(firstValue(record, titlePaths[module] || ['id']));
  if (module === 'commissions' && /^\d+$/.test(title)) {
    return `Comissão #${title}`;
  }

  return title;
};

const recordSubtitle = (module, record) => {
  const values = {
    sales: firstValue(record, [
      'address.endereco',
      'manager.name',
      'sales_stage_label',
    ]),
    proposals: firstValue(record, [
      'contact.name',
      'contact.full_name',
      'status_label',
      'status',
    ]),
    visits: firstValue(record, [
      'scheduled_at',
      'starts_at',
      'status_label',
      'status',
    ]),
    postSale: firstValue(record, [
      'transaction.address.endereco',
      'responsible.name',
      'status_label',
    ]),
    finance: firstValue(record, [
      'parcela.endereco.logradouro',
      'dados_processo.manager.name',
      'parcela.status.label',
      'endereco.logradouro',
      'manager.name',
      'status.label',
    ]),
    brokers: firstValue(record, ['role', 'profile', 'status', 'email']),
    commissions: firstValue(record, [
      'status.label',
      'status',
      'beneficiary.name',
      'side',
    ]),
  };

  const value = values[module];
  return /(_at|_on)$/.test(value?.toString() || '')
    ? dateTime(value)
    : stringValue(value);
};

const recordStatus = record => {
  const status = firstValue(record, [
    'status.label',
    'parcela.status.label',
    'status_label',
    'sales_stage_label',
    'status',
    'state',
  ]);
  return stringValue(status).replaceAll('_', ' ') || 'Sem status';
};

const recordAmount = (module, record) => {
  const paths = {
    sales: ['contracted_total', 'financial.contracted_total', 'total_amount'],
    proposals: ['offer_amount', 'amount', 'value'],
    visits: ['estimated_value', 'value'],
    postSale: ['transaction.contracted_total', 'contracted_total'],
    finance: ['valor_parcela', 'parcela.valor_parcela', 'amount', 'value'],
    brokers: ['commission_amount', 'total_amount'],
    commissions: ['amount', 'commission_amount', 'valor', 'value'],
  };

  const value = firstValue(record, paths[module] || []);
  return value === '' ? '—' : money(value);
};

const recordsFor = (module, state) => {
  if (module === 'commissions') {
    return state.records?.length ? state.records : state.agreements || [];
  }
  return state.records || [];
};

const historyFor = (module, state, detail) => {
  if (module === 'proposals') return state.timeline || [];
  if (module === 'visits') return detail?.timeline || detail?.events || [];
  if (module === 'finance') return detail?.history || state.history || [];
  return state.history || detail?.events || [];
};

const historyLabel = event =>
  stringValue(
    firstValue(event, [
      'label',
      'action',
      'event_type',
      'tipo',
      'notes',
      'reason',
      'status_novo',
    ])
  ).replaceAll('_', ' ') || 'Atualização registrada';

const historyDate = event =>
  dateTime(
    firstValue(event, ['occurred_at', 'created_at', 'updated_at', 'at'])
  );

const numericValue = value => {
  if (typeof value === 'object')
    return Number(value.amount ?? value.value ?? 0);
  return Number(value || 0);
};

const totalAmount = (module, records) =>
  records.reduce((sum, record) => {
    const paths = {
      sales: ['contracted_total', 'financial.contracted_total', 'total_amount'],
      proposals: ['offer_amount', 'amount', 'value'],
      finance: ['valor_parcela.amount', 'valor_parcela.amount_cents', 'amount'],
      commissions: ['amount', 'commission_amount', 'valor.amount', 'value'],
    };
    return sum + numericValue(firstValue(record, paths[module] || []));
  }, 0);

const metricsFor = (module, state, records) => {
  const total = records.length;

  if (module === 'sales') {
    return [
      {
        label: 'Em revisão',
        value: String(countBy(records, status => status.includes('revis'))),
        icon: 'i-lucide-search-check',
      },
      {
        label: 'Entregues',
        value: String(countBy(records, status => status.includes('entreg'))),
        icon: 'i-lucide-package-check',
      },
      {
        label: 'Finalizadas',
        value: String(countBy(records, status => status.includes('finaliz'))),
        icon: 'i-lucide-circle-check-big',
      },
      {
        label: 'Valor no funil',
        value: money(totalAmount(module, records)),
        icon: 'i-lucide-wallet-cards',
      },
    ];
  }

  if (module === 'proposals') {
    return [
      {
        label: 'Em negociação',
        value: String(
          countBy(
            records,
            status => status.includes('pend') || status.includes('counter')
          )
        ),
        icon: 'i-lucide-handshake',
      },
      {
        label: 'Aceitas',
        value: String(countBy(records, status => status.includes('accept'))),
        icon: 'i-lucide-badge-check',
      },
      {
        label: 'Recusadas',
        value: String(countBy(records, status => status.includes('declin'))),
        icon: 'i-lucide-circle-x',
      },
      {
        label: 'Valor proposto',
        value: money(totalAmount(module, records)),
        icon: 'i-lucide-banknote',
      },
    ];
  }

  if (module === 'visits') {
    return [
      {
        label: 'Agenda',
        value: String(state.agenda?.length || total),
        icon: 'i-lucide-calendar-days',
      },
      {
        label: 'Confirmadas',
        value: String(countBy(records, status => status.includes('confirm'))),
        icon: 'i-lucide-badge-check',
      },
      {
        label: 'Concluídas',
        value: String(
          countBy(
            records,
            status => status.includes('concl') || status.includes('finaliz')
          )
        ),
        icon: 'i-lucide-circle-check-big',
      },
      {
        label: 'Aguardando',
        value: String(
          countBy(
            records,
            status => status.includes('agend') || status.includes('solicit')
          )
        ),
        icon: 'i-lucide-clock-3',
      },
    ];
  }

  if (module === 'postSale') {
    const counts = state.counts || {};
    return [
      {
        label: 'Ativos',
        value: String(counts.ativos ?? total),
        icon: 'i-lucide-route',
      },
      {
        label: 'No prazo',
        value: String(counts.verdes ?? 0),
        icon: 'i-lucide-circle-check-big',
      },
      {
        label: 'Atenção',
        value: String(counts.amarelos ?? counts.laranjas ?? 0),
        icon: 'i-lucide-triangle-alert',
      },
      {
        label: 'Em atraso',
        value: String(counts.vermelhos ?? 0),
        icon: 'i-lucide-circle-alert',
      },
    ];
  }

  if (module === 'finance') {
    return [
      {
        label: 'Parcelas',
        value: String(total),
        icon: 'i-lucide-receipt-text',
      },
      {
        label: 'A receber',
        value: String(
          countBy(
            records,
            status => status.includes('receb') || status.includes('pend')
          )
        ),
        icon: 'i-lucide-arrow-down-to-line',
      },
      {
        label: 'Concluídas',
        value: String(
          countBy(
            records,
            status => status.includes('concl') || status.includes('pago')
          )
        ),
        icon: 'i-lucide-badge-check',
      },
      {
        label: 'Valor listado',
        value: money(totalAmount(module, records)),
        icon: 'i-lucide-landmark',
      },
    ];
  }

  if (module === 'brokers') {
    const dashboard = state.dashboard || {};
    const impact = state.profile?.impact_summary || {};
    return [
      {
        label: 'Corretores',
        value: String(impact.corretores_count ?? total),
        icon: 'i-lucide-users-round',
      },
      {
        label: 'Anúncios ativos',
        value: String(
          impact.published_listings_count ??
            dashboard.active_count ??
            dashboard.active ??
            '—'
        ),
        icon: 'i-lucide-sparkles',
      },
      {
        label: 'Imóveis',
        value: String(impact.listings_count ?? '—'),
        icon: 'i-lucide-building-2',
      },
      {
        label: 'Filiais ativas',
        value: String(impact.active_branches_count ?? '—'),
        icon: 'i-lucide-network',
      },
    ];
  }

  const totals = state.summary?.totais || {};

  return [
    {
      label: 'Comissões',
      value: String(totals.qtd ?? total),
      icon: 'i-lucide-chart-no-axes-combined',
    },
    {
      label: 'Conciliação',
      value: String(state.reconciliation?.length || 0),
      icon: 'i-lucide-scale',
    },
    {
      label: 'Lançamentos',
      value: String(state.ledger?.length || 0),
      icon: 'i-lucide-notebook-tabs',
    },
    {
      label: 'Valor listado',
      value: money(totals.valor ?? totalAmount(module, records)),
      icon: 'i-lucide-wallet-cards',
    },
  ];
};

const createFields = Object.freeze({
  sales: [
    {
      key: 'imovel_listing_id',
      label: 'ID do anúncio do imóvel',
      type: 'number',
      required: true,
    },
    {
      key: 'kind',
      label: 'Tipo de operação',
      type: 'select',
      options: ['venda'],
      required: true,
    },
    { key: 'title', label: 'Título da venda', type: 'text' },
    { key: 'contracted_total', label: 'Valor contratado', type: 'number' },
    {
      key: 'currency',
      label: 'Moeda',
      type: 'select',
      options: ['BRL'],
      required: true,
    },
  ],
  proposals: [
    { key: 'imovel_id', label: 'ID do imóvel', type: 'number', required: true },
    {
      key: 'contact_id',
      label: 'ID do contato',
      type: 'number',
      required: true,
    },
    {
      key: 'offer_amount',
      label: 'Valor da proposta',
      type: 'number',
      required: true,
    },
    {
      key: 'kind',
      label: 'Tipo',
      type: 'select',
      options: ['offer', 'booking'],
      required: true,
    },
    {
      key: 'business_context',
      label: 'Contexto comercial',
      type: 'select',
      options: ['purchase', 'rental'],
      required: true,
    },
    {
      key: 'payment_kind',
      label: 'Forma de pagamento',
      type: 'select',
      options: [
        'a_vista',
        'financiamento',
        'a_vista_fgts',
        'financiamento_fgts',
      ],
      required: true,
    },
    { key: 'notes', label: 'Observações', type: 'textarea' },
  ],
  visits: [
    { key: 'imovel_id', label: 'ID do imóvel', type: 'number', required: true },
    {
      key: 'contact_id',
      label: 'ID do contato',
      type: 'number',
      required: true,
    },
    {
      key: 'scheduled_at',
      label: 'Início',
      type: 'datetime-local',
      required: true,
    },
    {
      key: 'ends_at',
      label: 'Término',
      type: 'datetime-local',
      required: true,
    },
    {
      key: 'visit_type',
      label: 'Tipo',
      type: 'select',
      options: ['presencial', 'video', 'message'],
      required: true,
    },
    {
      key: 'buyer_visit_intention',
      label: 'Intenção do cliente',
      type: 'select',
      options: ['indefinido'],
      required: true,
    },
    {
      key: 'timezone',
      label: 'Fuso horário',
      type: 'select',
      options: ['America/Sao_Paulo'],
      required: true,
    },
  ],
  postSale: [
    {
      key: 'property_transaction_id',
      label: 'ID da venda',
      type: 'number',
      required: true,
    },
    { key: 'responsible_user_id', label: 'ID do responsável', type: 'number' },
    { key: 'manager_user_id', label: 'ID do gestor', type: 'number' },
    { key: 'status_due_at', label: 'Prazo da etapa', type: 'datetime-local' },
    {
      key: 'priority',
      label: 'Prioridade',
      type: 'select',
      options: ['normal', 'alta', 'urgente'],
    },
  ],
  commissions: [
    {
      key: 'imovel_listing_id',
      label: 'ID do imóvel',
      type: 'number',
      required: true,
    },
    {
      key: 'side',
      label: 'Lado',
      type: 'select',
      options: ['seller', 'buyer'],
      required: true,
    },
    {
      key: 'beneficiary_kind',
      label: 'Beneficiário',
      type: 'select',
      options: ['corretor', 'equipe', 'imobiliaria'],
      required: true,
    },
    {
      key: 'commission_kind',
      label: 'Cálculo',
      type: 'select',
      options: ['percentage', 'fixed'],
      required: true,
    },
    {
      key: 'payment_event',
      label: 'Evento de pagamento',
      type: 'select',
      options: [
        'on_signing',
        'on_deed',
        'on_first_rent',
        'on_closing',
        'on_installments',
        'custom',
      ],
      required: true,
    },
    { key: 'commission_percentage', label: 'Percentual', type: 'number' },
    { key: 'commission_amount', label: 'Valor fixo', type: 'number' },
    {
      key: 'beneficiary_user_id',
      label: 'ID do usuário beneficiário',
      type: 'number',
    },
    {
      key: 'beneficiary_team_id',
      label: 'ID da equipe beneficiária',
      type: 'number',
    },
  ],
});

const defaultForm = module => {
  const defaults = {
    beneficiary_kind: 'corretor',
    business_context: 'purchase',
    commission_kind: 'percentage',
    currency: 'BRL',
    kind: 'offer',
    payment_event: 'on_closing',
    payment_kind: 'a_vista',
    timezone: 'America/Sao_Paulo',
    visit_type: 'presencial',
    buyer_visit_intention: 'indefinido',
  };
  if (module === 'sales') defaults.kind = 'venda';
  const fields = Object.fromEntries(
    (createFields[module] || []).map(field => [
      field.key,
      defaults[field.key] || '',
    ])
  );

  return {
    ...fields,
    counter_expires_at: '',
    counter_guarantee: '',
    counter_offer_amount: '',
    cancelled_at: '',
    next_action: '',
    outcome: '',
    payment_responsibility_id: '',
    rescheduled_at: '',
    responsible_user_id: '',
  };
};

const inputDateTime = value => {
  if (!value) return '';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value).slice(0, 16);

  const pad = number => String(number).padStart(2, '0');
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(
    parsed.getDate()
  )}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};

const formValueFor = (record, field) => {
  const valuePaths = {
    beneficiary_team_id: ['beneficiary_team_id', 'beneficiary_team.id'],
    beneficiary_user_id: ['beneficiary_user_id', 'beneficiary_user.id'],
    contact_id: ['contact_id', 'contact.id', 'client.id'],
    ends_at: ['ends_at', 'scheduled_end_at', 'end_at'],
    imovel_id: ['imovel_id', 'property_id', 'imovel.id', 'property.id'],
    offer_amount: ['offer_amount', 'amount', 'amount_cents'],
    property_transaction_id: [
      'property_transaction_id',
      'transaction_id',
      'transaction.id',
    ],
    scheduled_at: ['scheduled_at', 'scheduled_for', 'starts_at'],
  };
  const value = firstValue(record, valuePaths[field.key] || [field.key]);

  if (field.type === 'datetime-local') return inputDateTime(value);
  return value == null ? '' : String(value);
};

const factValue = (record, paths, type = 'text') => {
  const value = firstValue(record, paths);
  if (value === '') return '';
  if (type === 'date') return dateTime(value);
  if (type === 'money') return money(value);
  return typeof value === 'object' ? '' : String(value);
};

const runtimeFactLabels = Object.freeze({
  beneficiary_kind: 'Tipo de beneficiário',
  commission_kind: 'Tipo de comissão',
  commission_percentage: 'Percentual da comissão',
  current_version_number: 'Versão atual',
  ends_at: 'Término',
  guarantee: 'Garantia',
  imovel_listing_id: 'Anúncio vinculado',
  imovel_id: 'Imóvel vinculado',
  kind: 'Tipo',
  offer_amount: 'Valor da proposta',
  payment_event: 'Evento de pagamento',
  priority: 'Prioridade',
  scheduled_at: 'Agendada para',
  settled: 'Liquidada',
  side: 'Lado',
  visit_type: 'Tipo da visita',
});

const ignoredRuntimeFactKeys = new Set([
  'account_id',
  'actions',
  'available_transitions',
  'created_at',
  'id',
  'metadata',
  'permissions',
  'transition_catalog',
  'updated_at',
]);

const readableRuntimeFact = (key, value) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (key.endsWith('_at')) return dateTime(value);
  if (key.endsWith('_amount') || key === 'commission_percentage') {
    return key === 'commission_percentage' ? `${value}%` : money(value);
  }
  return typeof value === 'object' ? '' : String(value);
};

const fallbackLabelFor = key =>
  runtimeFactLabels[key] ||
  key.replaceAll('_', ' ').replace(/\b\w/g, letter => letter.toUpperCase());

const runtimeFactsFrom = (record, existingFacts) => {
  const existingValues = new Set(existingFacts.map(fact => fact.value));
  const facts = [];
  const walk = (value, depth = 0) => {
    if (!value || typeof value !== 'object' || depth > 1) return;

    Object.entries(value).forEach(([key, nestedValue]) => {
      if (ignoredRuntimeFactKeys.has(key) || key.endsWith('_id')) return;
      if (Array.isArray(nestedValue)) return;
      if (nestedValue && typeof nestedValue === 'object') {
        walk(nestedValue, depth + 1);
        return;
      }

      const valueText = readableRuntimeFact(key, nestedValue);
      if (!valueText || existingValues.has(valueText)) return;

      existingValues.add(valueText);
      facts.push({ label: fallbackLabelFor(key), value: valueText });
    });
  };

  walk(record);
  return facts;
};

const detailFactsFor = (module, record) => {
  const definitions = {
    brokers: [
      ['E-mail', ['email', 'user.email']],
      ['Função', ['role', 'user.role']],
      ['Anúncios ativos', ['active_listings']],
      ['Atualizado', ['updated_at'], 'date'],
    ],
    commissions: [
      [
        'Beneficiário',
        [
          'beneficiary.name',
          'user.name',
          'recipient.name',
          'beneficiary_user_id',
        ],
      ],
      ['Vencimento', ['due_at', 'scheduled_for', 'expected_at'], 'date'],
      [
        'Valor da comissão',
        [
          'commission_amount',
          'settled_amount',
          'net_amount',
          'net_amount_cents',
        ],
        'money',
      ],
      ['Atualizado', ['updated_at'], 'date'],
    ],
    finance: [
      [
        'Vencimento',
        ['due_at', 'due_date', 'scheduled_for', 'parcela.data_prevista'],
        'date',
      ],
      [
        'Origem',
        ['origin', 'source', 'processo.codigo', 'dados_processo.codigo'],
      ],
      [
        'Favorecido',
        [
          'payee.name',
          'beneficiary.name',
          'recipient.name',
          'dados_processo.manager.name',
        ],
      ],
      ['Atualizado', ['updated_at'], 'date'],
    ],
    postSale: [
      ['Prazo', ['status_due_at', 'due_at', 'next_due_at'], 'date'],
      ['Responsável', ['responsible.name', 'responsible_user.name']],
      ['Gestor', ['manager.name', 'manager_user.name']],
      ['Atualizado', ['updated_at'], 'date'],
    ],
    proposals: [
      ['Cliente', ['contact.name', 'client.name', 'buyer.name']],
      ['Válida até', ['expires_at', 'current_revision.expires_at'], 'date'],
      ['Tipo', ['kind', 'property_type']],
      ['Criada em', ['created_at', 'submitted_at'], 'date'],
    ],
    sales: [
      ['Cliente', ['buyer.name', 'client.name', 'contact.name']],
      ['Responsável', ['manager.name', 'responsible.name', 'seller.name']],
      ['Previsão', ['estimated_signature_at', 'expected_at', 'due_at'], 'date'],
      ['Atualizada em', ['updated_at'], 'date'],
    ],
    visits: [
      ['Agendada para', ['scheduled_at', 'scheduled_for', 'starts_at'], 'date'],
      ['Cliente', ['contact.name', 'client.name', 'visitor.name']],
      ['Responsável', ['agent.name', 'broker.name', 'assigned_to.name']],
      ['Atualizada em', ['updated_at'], 'date'],
    ],
  };

  const primaryFacts = (definitions[module] || [])
    .map(([label, paths, type]) => ({
      label,
      value: factValue(record, paths, type),
    }))
    .filter(item => item.value && item.value !== '—');

  return [...primaryFacts, ...runtimeFactsFrom(record, primaryFacts)].slice(
    0,
    12
  );
};

const actionPayload = (action, detail, form) => {
  if (action === 'requestReview') return [[], form.reason || ''];
  if (action === 'saveDraft' || action === 'deliver') return [{}];
  if (action === 'finish') return [{ notes: form.reason || undefined }];
  if (action === 'reschedule') {
    return [
      {
        reason: form.reason || undefined,
        scheduled_at: form.rescheduled_at,
        step: 'confirm',
      },
    ];
  }
  if (action === 'finalizeOutcome') {
    return [
      {
        next_action: form.next_action || undefined,
        outcome: form.outcome,
        reason: form.reason || undefined,
      },
    ];
  }
  if (action === 'transition') {
    return [{ status: form.status, reason: form.reason || undefined }];
  }
  if (action === 'cancel') {
    return [
      {
        data_cancelamento: form.cancelled_at,
        reason: form.reason,
      },
    ];
  }
  if (action === 'assign') {
    return [
      {
        reason: form.reason || undefined,
        responsible_user_id: form.responsible_user_id,
      },
    ];
  }
  if (action === 'addNote') return [{ note: form.reason }];
  if (action === 'confirmPayment') {
    return [
      form.payment_responsibility_id,
      { notes: form.reason || undefined },
    ];
  }
  if (['accept', 'decline', 'withdraw'].includes(action)) {
    return [{ notes: form.reason || undefined }];
  }
  if (action === 'counter') {
    const currentRevisionId = firstValue(detail, [
      'current_revision.id',
      'currentRevision.id',
    ]);
    return [
      {
        expected_revision_id: currentRevisionId || undefined,
        notes: form.reason || undefined,
        property_offer_revision: {
          expires_at: form.counter_expires_at || undefined,
          offer_amount: Number(form.counter_offer_amount) || undefined,
          terms: form.counter_guarantee
            ? { guarantee: form.counter_guarantee }
            : undefined,
        },
      },
    ];
  }
  if (['markPayable', 'markPaid'].includes(action)) {
    return [
      {
        idempotency_key:
          window.crypto?.randomUUID?.() ||
          `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      },
    ];
  }
  if (action === 'publishAgreement') return [];
  return [];
};

const isPermissionEnabled = (permissions, key) =>
  permissions && permissions[key] === true;

export function useImudeiRuntimeModel({ realData, module, screen }) {
  const form = reactive({ reason: '', status: '' });
  const notice = ref('');
  const localError = ref('');
  const query = ref('');

  const copy = computed(() => MODULE_COPY[module.value] || MODULE_COPY.sales);
  const state = computed(() => realData.data.value || {});
  const sourceRecords = computed(() => recordsFor(module.value, state.value));
  const records = computed(() =>
    sourceRecords.value
      .map(record => ({
        amount: recordAmount(module.value, record),
        id: stringValue(idFor(record)),
        source: record,
        status: recordStatus(record),
        subtitle: recordSubtitle(module.value, record),
        title: recordTitle(module.value, record),
      }))
      .filter(record =>
        `${record.title} ${record.subtitle} ${record.status}`
          .toLocaleLowerCase('pt-BR')
          .includes(query.value.toLocaleLowerCase('pt-BR'))
      )
  );
  const selectedRecord = computed(() => {
    const fromDetail = state.value.detail;
    const selectedId = stringValue(realData.selectedId?.value);
    const fromList = sourceRecords.value.find(
      record => stringValue(idFor(record)) === selectedId
    );
    return fromDetail || fromList || null;
  });
  const detail = computed(() => {
    const record = selectedRecord.value;
    if (!record) return null;
    return {
      amount: recordAmount(module.value, record),
      id: stringValue(idFor(record)),
      source: record,
      status: recordStatus(record),
      subtitle: recordSubtitle(module.value, record),
      title: recordTitle(module.value, record),
    };
  });
  const history = computed(() =>
    asArray(historyFor(module.value, state.value, selectedRecord.value)).map(
      event => ({
        actor: stringValue(
          firstValue(
            event,
            ['actor.name', 'user.name', 'author.name'],
            'Sistema'
          )
        ),
        date: historyDate(event),
        id: stringValue(
          firstValue(event, ['id', 'sequence'], historyLabel(event))
        ),
        label: historyLabel(event),
      })
    )
  );
  const metrics = computed(() =>
    metricsFor(module.value, state.value, sourceRecords.value)
  );
  const fields = computed(() => createFields[module.value] || []);
  const createActionName = computed(() =>
    module.value === 'commissions' ? 'createAgreement' : 'create'
  );
  const canCreate = computed(
    () =>
      fields.value.length > 0 &&
      typeof realData.actions[module.value]?.[createActionName.value] ===
        'function'
  );
  const editableFields = computed(() =>
    realData.actions[module.value]?.update ? fields.value : []
  );
  const canEdit = computed(() => editableFields.value.length > 0);
  const actionOptions = computed(() => {
    const record = selectedRecord.value;
    if (!record) return [];
    const permissions = record.permissions || record.actions || {};
    const options = (actionConfig[module.value] || [])
      .filter(([name, permission]) => {
        if (!isPermissionEnabled(permissions, permission)) return false;
        if (name !== 'counter') return true;

        return Boolean(
          firstValue(record, ['current_revision.id', 'currentRevision.id'])
        );
      })
      .map(([name]) => ({ name, label: actionLabel[name] }));

    if (module.value === 'commissions') {
      const agreement = state.value.agreements?.find(
        item => stringValue(idFor(item)) === stringValue(idFor(record))
      );
      if (
        agreement?.permissions?.can_publish ||
        agreement?.status === 'draft'
      ) {
        options.push({
          name: 'publishAgreement',
          label: actionLabel.publishAgreement,
        });
      }
    }

    return options;
  });
  const transitionOptions = computed(() => {
    const record = selectedRecord.value || {};
    return asArray(
      firstValue(record, [
        'transition_catalog',
        'available_transitions',
        'status_catalog',
      ])
    ).map(item => ({
      label: item.label || item.name || item.status,
      value: item.id || item.status || item.slug,
    }));
  });
  const hasData = computed(() => sourceRecords.value.length > 0);
  const detailFacts = computed(() =>
    detail.value ? detailFactsFor(module.value, detail.value.source) : []
  );
  const canSubmit = computed(() =>
    screen.value === 'create' ? canCreate.value : canEdit.value
  );
  const unavailableMessage = computed(() => {
    if (screen.value === 'create' && !canCreate.value) {
      return `O backend atual não oferece criação de ${copy.value.singular} por esta rota.`;
    }

    if (screen.value === 'edit' && !canEdit.value) {
      return `O backend atual não oferece edição direta de ${copy.value.singular} por esta rota.`;
    }

    return '';
  });

  const resetForm = () => {
    Object.assign(form, {
      reason: '',
      status: '',
      ...defaultForm(module.value),
    });
    notice.value = '';
    localError.value = '';
  };

  const hydrateDetailForm = () => {
    const record = selectedRecord.value;
    if (!record) return;

    const values = editableFields.value.reduce((result, field) => {
      result[field.key] = formValueFor(record, field);
      return result;
    }, {});
    Object.assign(form, values);

    if (module.value === 'proposals') {
      form.counter_offer_amount = String(
        firstValue(record, ['current_revision.offer_amount', 'offer_amount']) ||
          ''
      );
      form.counter_expires_at = String(
        firstValue(record, ['current_revision.expires_at']) || ''
      ).slice(0, 10);
      form.counter_guarantee = String(
        firstValue(record, ['current_revision.terms.guarantee']) || ''
      );
    }
  };

  const create = async () => {
    if (!canCreate.value) return null;

    localError.value = '';
    notice.value = '';
    const payload = fields.value.reduce((result, field) => {
      if (form[field.key] !== '') result[field.key] = form[field.key];
      return result;
    }, {});

    try {
      const response =
        await realData.actions[module.value][createActionName.value](payload);
      notice.value = `A ${copy.value.singular} foi criada com sucesso.`;
      return response;
    } catch (error) {
      localError.value = realData.error.value;
      return null;
    }
  };

  const runAction = async action => {
    const record = detail.value;
    if (!record || !realData.actions[module.value]?.[action]) return null;

    localError.value = '';
    notice.value = '';
    try {
      const actionId =
        action === 'markPayable' || action === 'markPaid'
          ? record.source.pool_id || record.id
          : record.id;
      const payload = actionPayload(action, record.source, form);
      const response = await realData.actions[module.value][action](
        actionId,
        ...payload
      );
      notice.value = `${actionLabel[action]} concluída.`;
      return response;
    } catch (error) {
      localError.value = realData.error.value;
      return null;
    }
  };

  const saveEdit = async () => {
    const record = detail.value;
    if (!record || !canEdit.value) return null;

    localError.value = '';
    notice.value = '';
    const payload = editableFields.value.reduce((result, field) => {
      if (form[field.key] !== '') result[field.key] = form[field.key];
      return result;
    }, {});

    try {
      const response = await realData.actions[module.value].update(
        record.id,
        payload
      );
      notice.value = 'As alterações foram salvas com sucesso.';
      return response;
    } catch (error) {
      localError.value = realData.error.value;
      return null;
    }
  };

  watch([module, screen], resetForm, { immediate: true });
  watch(
    () => detail.value?.id,
    () => hydrateDetailForm(),
    { immediate: true }
  );

  return {
    actionOptions,
    canEdit,
    canCreate,
    canSubmit,
    copy,
    create,
    detail,
    detailFacts,
    editableFields,
    fields,
    form,
    hasData,
    history,
    localError,
    metrics,
    notice,
    query,
    records,
    runAction,
    saveEdit,
    transitionOptions,
    unavailableMessage,
  };
}
